import * as React from 'react';
import { Field, FieldProps, getIn, FieldConfig } from 'formik';
import { Form } from 'antd';
import { FormItemProps } from 'antd/lib/form/FormItem';
import { isEqual } from 'lodash';
import { isComponentsEqual } from 'utils/reactUtils';

type Props = {
  showValidateSuccess?: boolean;
  showInitialErrorAfterTouched?: boolean;
  children: React.ReactNode;
  showValidateError?: boolean;
} & { name: string } & FormItemProps &
  Pick<FieldConfig, 'validate'>;

type CustomFormItemProps = Props & FieldProps;

function areEqual(
  prevProps: CustomFormItemProps,
  nextProps: CustomFormItemProps,
) {
  const {
    children: prevChildren,
    form: prevForm,
    field: prevField,
    ...restPrevProps
  } = prevProps;
  const {
    children: nextChildren,
    form: nextForm,
    field: nextField,
    ...restNextProps
  } = nextProps;

  return (
    isEqual(restPrevProps, restNextProps) &&
    isEqual(prevForm.errors, nextForm.errors) &&
    isEqual(prevForm.touched, nextForm.touched) &&
    isComponentsEqual(prevChildren, nextChildren)
  );
}

const CustomFormItem = React.memo(
  ({
    field: { name },
    form: { errors = {}, touched = {}, initialErrors = {} },
    showInitialErrorAfterTouched,
    showValidateSuccess,
    showValidateError = true,
    ...props
  }: CustomFormItemProps) => {
    const error = getIn(errors, name, undefined);
    const initialError = getIn(initialErrors, name, undefined);
    // The value could be an empty array
    const isTouched = Boolean(getIn(touched, name, false));
    const hasError = error !== undefined && isTouched;
    const hasInitialError = initialError !== undefined;
    const isValid = !error && isTouched;
    const showHelp =
      hasError ||
      (hasInitialError && (!isTouched || showInitialErrorAfterTouched));

    return (
      <Form.Item
        validateStatus={
          !showValidateError
            ? undefined
            : hasError || (hasInitialError && !isTouched)
              ? 'error'
              : isValid && showValidateSuccess
                ? 'success'
                : undefined
        }
        hasFeedback={isValid}
        help={
          showValidateError &&
          showHelp && (
            <>
              {hasError && <li>{error}</li>}
              {hasInitialError &&
                (!isTouched || showInitialErrorAfterTouched) && (
                  <li>{initialError}</li>
                )}
            </>
          )
        }
        {...props}
      >
        {props.children}
      </Form.Item>
    );
  },
  areEqual,
);

export default function FormItem(props: Props) {
  return (
    <Field
      validate={props.validate}
      component={CustomFormItem}
      {...props}
      name={props.name}
    />
  );
}
